import { ActivitySpinnerSize, ActivitySpinnerWidget, TextFieldWidget } from '@tableau/widgets';
import * as React from 'react';

import { Messages } from '~src/codegen/Localize';

import { IAdminProfile } from '../models/IAdminProfile';
import { AdminProfileStyles } from '../styles/AdminProfileStyles';
import { AdminProfileListItem } from './AdminProfileListItem';

export interface AdminProfileListProps {
  profiles: ReadonlyArray<IAdminProfile>;
  isLoading?: boolean;
  selectedProfileId: string | null;
  onSelectProfile: (profile: IAdminProfile) => void;
  onEditProfile?: (profile: IAdminProfile) => void;
  onDeleteProfile?: (profile: IAdminProfile) => void;
}

export const AdminProfileList: React.FC<AdminProfileListProps> = ({
  profiles,
  isLoading = false,
  selectedProfileId,
  onSelectProfile,
  onEditProfile,
  onDeleteProfile,
}) => {
  const [searchQuery, setSearchQuery] = React.useState('');

  const filteredProfiles = React.useMemo(() => {
    if (!profiles) {
      return [];
    }

    // Create a copy to avoid mutating props
    let result = profiles.slice();

    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      result = result.filter(profile =>
        profile.name.toLowerCase().includes(query) ||
        profile.description.toLowerCase().includes(query)
      );
    }

    // Sort by name alphabetically
    result.sort((a, b) => a.name.localeCompare(b.name));

    return result;
  }, [profiles, searchQuery]);

  const handleSearchChange = React.useCallback((value: string) => {
    setSearchQuery(value);
  }, []);

  return (
    <div className={AdminProfileStyles.ListWithSearchWrapper}>
      <div className={AdminProfileStyles.SearchBox}>
        <TextFieldWidget
          kind="search"
          label={Messages.adminProfilesSearchAriaLabel()}
          value={searchQuery}
          onChange={(e) => handleSearchChange(e.target.value)}
          placeholder={Messages.adminProfilesSearchPlaceholder()}
          data-tb-test-id="admin-profile-search"
        />
      </div>

      <div
        className={AdminProfileStyles.ListScrollArea}
        role="list"
        aria-label={Messages.adminProfilesSearchAriaLabel()}
      >
        {isLoading ? (
          <div className={AdminProfileStyles.EmptyState}>
            <ActivitySpinnerWidget
              testId="admin-profiles-list-loading-spinner"
              spinnerSize={ActivitySpinnerSize.Large}
            />
          </div>
        ) : !filteredProfiles || filteredProfiles.length === 0 ? (
          <div className={AdminProfileStyles.EmptyState}>
            <div className={AdminProfileStyles.EmptyStateTitle}>
              {Messages.adminProfilesEmptyTitle()}
            </div>
            <div className={AdminProfileStyles.EmptyStateDescription}>
              {searchQuery ? Messages.adminProfilesEmptySearchDescription() : Messages.adminProfilesEmptyNoProfilesDescription()}
            </div>
          </div>
        ) : (
          filteredProfiles.map(profile => (
            <AdminProfileListItem
              key={profile.id}
              profile={profile}
              isSelected={profile.luid === selectedProfileId}
              onClick={onSelectProfile}
              onEdit={onEditProfile}
              onDelete={onDeleteProfile}
            />
          ))
        )}
      </div>
    </div>
  );
};

AdminProfileList.displayName = 'AdminProfileList';
